/**
 * Voice Conversation Button
 *
 * Button for the full-duplex voice conversation feature.
 * Supports two interaction modes:
 *   - Push-to-talk (PTT): Hold (>=300ms) to talk, release to stop; or
 *     click (<300ms) to toggle listening on/off
 *   - Open-mic: Click to enable/disable. VAD handles speech detection.
 *
 * Visual states reflect the engine state machine:
 *   idle → connecting → listening → processing → speaking
 *
 * Accessibility:
 *   - Dynamic aria-label for screen readers
 *   - prefers-reduced-motion: disables pulse/glow animations
 *   - Keyboard accessible (Space/Enter via native button behavior)
 */

import { useCallback, useRef } from 'react'
import { cn } from '@/utils'
import { Mic, AudioLines, Square, Radio } from 'lucide-react'
import { Spinner, StatusDot } from '@/components/ui'
import type { VoiceConversationState, VoiceConversationMode } from '@/types/voice'

interface VoiceConversationButtonProps {
  /** Current engine state */
  state: VoiceConversationState
  /** Audio input level 0–1 */
  audioLevel: number
  /** Current interaction mode */
  mode: VoiceConversationMode
  /** Whether open-mic session is active */
  isOpenMicActive: boolean
  /** Whether VAD detected active speech (open-mic only) */
  isSpeechDetected: boolean
  /** Start listening callback (PTT) */
  onStartListening: () => Promise<void>
  /** Stop listening callback (PTT) */
  onStopListening: () => Promise<void>
  /** Interrupt AI speech */
  onInterrupt: () => void
  /** Cancel operation */
  onCancel: () => void
  /** Toggle open-mic mode */
  onToggleOpenMic: () => Promise<void>
  /** Whether the feature is enabled */
  disabled?: boolean
  className?: string
}

/** Threshold to distinguish hold from toggle (ms) */
const HOLD_THRESHOLD_MS = 300

/**
 * A click this soon after a hold release is the tail of that release, not a
 * new press. Covers a touch tap's delayed click, and stays short so a pointer
 * that left mid-hold (no click follows a leave) can't swallow a later real
 * click such as a keyboard press.
 */
const CLICK_AFTER_RELEASE_MS = 1_000

export function VoiceConversationButton({
  state,
  audioLevel,
  mode,
  isOpenMicActive,
  isSpeechDetected,
  onStartListening,
  onStopListening,
  onInterrupt,
  onCancel,
  onToggleOpenMic,
  disabled = false,
  className,
}: VoiceConversationButtonProps) {
  const pressStartRef = useRef(0)
  const isHoldingRef = useRef(false)
  // Track if we started in toggle mode (short click) - click again to stop
  const isToggleModeRef = useRef(false)
  // When the last hold was released (ms; 0 = none). The click that completes
  // the hold consumes it.
  const holdReleasedAtRef = useRef(0)

  // -----------------------------------------------------------------------
  // PTT event handlers (only active in push-to-talk mode)
  // -----------------------------------------------------------------------

  const handlePointerDown = useCallback(() => {
    if (disabled || mode === 'open-mic') return
    holdReleasedAtRef.current = 0

    if (state === 'idle' || state === 'error' || state === 'speaking') {
      // When speaking, startListening() will interrupt the AI first
      pressStartRef.current = Date.now()
      isHoldingRef.current = true
      onStartListening()
    }
  }, [state, disabled, mode, onStartListening])

  const handlePointerUp = useCallback(() => {
    if (mode === 'open-mic') return
    if (!isHoldingRef.current) return

    const holdDuration = Date.now() - pressStartRef.current

    if (holdDuration >= HOLD_THRESHOLD_MS) {
      // Hold mode: release ends the turn, whatever state it has reached
      // (still connecting, or already ended by the silence ceiling)
      isHoldingRef.current = false
      isToggleModeRef.current = false
      holdReleasedAtRef.current = Date.now()
      onStopListening()
    } else {
      // Toggle mode (short click): mark as toggle mode, user clicks again to stop
      // Keep isHoldingRef true briefly so handleClick knows we just started
      isToggleModeRef.current = true
    }
  }, [mode, onStopListening])

  const handleClick = useCallback(() => {
    if (disabled) return

    // The click that completes a hold is part of its release, already handled
    const releasedAt = holdReleasedAtRef.current
    holdReleasedAtRef.current = 0
    if (releasedAt !== 0 && Date.now() - releasedAt < CLICK_AFTER_RELEASE_MS) return

    // The click that completes a short press is the toggle's start, in any state
    if (mode !== 'open-mic' && isHoldingRef.current && isToggleModeRef.current) {
      isHoldingRef.current = false
      return
    }

    // Open-mic mode: toggle on/off
    if (mode === 'open-mic') {
      if (state === 'speaking') {
        onInterrupt()
      } else if (state === 'connecting' || state === 'processing') {
        onCancel()
      } else {
        onToggleOpenMic()
      }
      return
    }

    // PTT mode: handle stop, cancel, and interrupt
    if (state === 'listening') {
      const timeSinceStart = Date.now() - pressStartRef.current

      // Skip if we just initiated listening in this click cycle (first click in toggle mode)
      // isHoldingRef is true means pointerDown just fired in this same click sequence
      // But if it's been more than 500ms since we started, allow stopping as escape hatch
      if (isHoldingRef.current && timeSinceStart < 500) {
        // This is the first click (start) - clear holding state and wait for second click
        isHoldingRef.current = false
        return
      }

      // Second click, escape hatch (>500ms), or force-stop - stop listening
      isToggleModeRef.current = false
      isHoldingRef.current = false
      onStopListening()
    } else if (state === 'connecting') {
      // The turn began at the press: a click here ends it, never discards it
      isToggleModeRef.current = false
      isHoldingRef.current = false
      onStopListening()
    } else if (state === 'processing') {
      onCancel()
    } else if (state === 'speaking') {
      // Allow interrupting AI speech with a click in PTT mode too
      onInterrupt()
    }
  }, [state, disabled, mode, onStopListening, onCancel, onInterrupt, onToggleOpenMic])

  // -----------------------------------------------------------------------
  // Visual configuration based on state + mode
  // -----------------------------------------------------------------------

  // NOTE: the vendored basis VoiceIndicator stays exported-but-UNUSED here — its
  // hero-ring geometry and listening=green / speaking=primary semantics don't fit
  // this app's 48px red-recording toolbar button (Phase 3.5 decision #3). The
  // per-state colors below use APP semantics on basis status tokens instead.
  const getStateConfig = () => {
    // Open-mic active states
    if (isOpenMicActive) {
      if (state === 'listening' && isSpeechDetected) {
        return {
          icon: <Mic className="w-5 h-5" />,
          label: 'Recording your speech',
          bgClass: 'bg-destructive hover:bg-destructive/90',
          pulseClass: 'motion-safe:animate-pulse',
          ringClass: '',
        }
      }
      if (state === 'listening') {
        return {
          icon: <Radio className="w-5 h-5" />,
          label: 'Open mic active — speak to interact. Click to disable.',
          bgClass: 'bg-success hover:bg-success/90',
          pulseClass: '',
          ringClass: 'ring-2 ring-success ring-offset-2 ring-offset-background',
        }
      }
      if (state === 'processing') {
        return {
          icon: <Spinner />,
          label: 'Processing your speech',
          bgClass: 'bg-info hover:bg-info/90',
          pulseClass: '',
          ringClass: 'ring-2 ring-success ring-offset-2 ring-offset-background',
        }
      }
      if (state === 'speaking') {
        return {
          icon: <AudioLines className="w-5 h-5" />,
          label: 'AI speaking — speak to interrupt',
          bgClass: 'bg-success hover:bg-success/90',
          pulseClass: '',
          ringClass: 'ring-2 ring-success ring-offset-2 ring-offset-background',
        }
      }
      if (state === 'connecting') {
        return {
          icon: <Spinner />,
          label: 'Connecting open mic...',
          bgClass: 'bg-warning hover:bg-warning/90',
          pulseClass: '',
          ringClass: '',
        }
      }
    }

    // PTT / default states
    switch (state) {
      case 'connecting':
        return {
          icon: <Spinner />,
          label: 'Connecting...',
          bgClass: 'bg-warning hover:bg-warning/90',
          pulseClass: '',
          ringClass: '',
        }
      case 'listening':
        return {
          icon: <Mic className="w-5 h-5" />,
          label: 'Listening for speech',
          bgClass: 'bg-destructive hover:bg-destructive/90',
          pulseClass: 'motion-safe:animate-pulse',
          ringClass: '',
        }
      case 'processing':
        return {
          icon: <Spinner />,
          label: 'Processing your speech',
          bgClass: 'bg-info hover:bg-info/90',
          pulseClass: '',
          ringClass: '',
        }
      case 'speaking':
        return {
          icon: <AudioLines className="w-5 h-5" />,
          label: 'AI is speaking, click to interrupt',
          bgClass: 'bg-success hover:bg-success/90',
          pulseClass: '',
          ringClass: '',
        }
      case 'error':
        return {
          icon: <Mic className="w-5 h-5" />,
          label: 'Error — click to retry',
          bgClass: 'bg-primary hover:bg-primary/90',
          fgClass: 'text-primary-foreground',
          pulseClass: '',
          ringClass: 'ring-2 ring-destructive ring-offset-2 ring-offset-background',
        }
      default: // idle
        return {
          icon: mode === 'open-mic'
            ? <Radio className="w-5 h-5" />
            : <Mic className="w-5 h-5" />,
          label: mode === 'open-mic'
            ? 'Enable open mic'
            : 'Push to talk',
          bgClass: 'bg-primary hover:bg-primary/90',
          fgClass: 'text-primary-foreground',
          pulseClass: '',
          ringClass: '',
        }
    }
  }

  const config = getStateConfig()

  // Audio level ring effect during listening (with active speech)
  const showLevelRing = state === 'listening' && (mode === 'push-to-talk' || isSpeechDetected)
  const levelRingSize = showLevelRing ? Math.max(0, audioLevel) : 0
  // Leave (Phase 3.5 §7): theme-independent audio-level glow — hardcoded rgba stays.
  const levelRingStyle =
    levelRingSize > 0
      ? {
          boxShadow: `0 0 0 ${Math.round(levelRingSize * 8)}px rgba(239, 68, 68, ${0.2 + levelRingSize * 0.3})`,
        }
      : undefined

  // Determine aria attributes
  const isToggle = mode === 'open-mic'

  return (
    <button
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onClick={handleClick}
      disabled={disabled}
      aria-label={config.label}
      aria-pressed={isToggle ? isOpenMicActive : undefined}
      role={isToggle ? 'switch' : undefined}
      style={levelRingStyle}
      className={cn(
        'relative h-12 w-12 rounded-xl flex items-center justify-center',
        'text-white',
        'motion-safe:transition-all motion-safe:duration-200',
        'motion-reduce:transition-none',
        'focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-ring',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'select-none touch-none',
        config.bgClass,
        config.fgClass,
        config.pulseClass,
        config.ringClass,
        className
      )}
    >
      {/* Cancel overlay for interruptible states */}
      {(state === 'connecting' || state === 'processing') && (
        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-foreground flex items-center justify-center">
          <Square className="w-2.5 h-2.5 text-background" />
        </span>
      )}

      {/* Open-mic badge */}
      {isOpenMicActive && state !== 'connecting' && (
        <StatusDot
          variant="online"
          aria-hidden
          className="absolute -top-1 -left-1 rounded-full border border-background"
        />
      )}

      {config.icon}
    </button>
  )
}
